import { Revenue } from '@/lib/types/definitions';

export const fetchImage2Base64 = async (url: string) => {
    try {
        // 1. 发起请求到远程 URL
        const response = await fetch(url);

        // 如果状态码不对，可以抛错或返回空
        if (!response.ok) {
            throw new Error(
                `Failed to fetch first frame, status: ${response.status}`,
            );
        }

        // 2. 获取二进制数据 (arrayBuffer)
        const arrayBuffer = await response.arrayBuffer();

        // 3. 转成 base64 字符串
        const base64Str = Buffer.from(arrayBuffer).toString('base64');
        // 拼成 dataURL
        const firstFrameBase64 = `data:image/jpeg;base64,${base64Str}`;

        // 返回给页面
        return firstFrameBase64;
    } catch (error) {
        console.error('Error fetching first frame:', error);
        // 如果出错，可以返回空字符串或替代图片
        return '';
    }
};

export const formatCurrency = (amount: number) => {
    return (amount / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
};

export const formatDateToYMD = (date: Date) =>
    new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'UTC',
    }).format(date);

export const formatDateToLocal = (
    dateStr: string,
    locale: string = 'en-US',
) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    return formatter.format(date);
};

export function formatMessageTime(timestamp: number | undefined): string {
    if (!timestamp) {
        return '';
    }
    const inputDate = new Date(timestamp);
    const currentDate = new Date();

    // Reset the hours, minutes, seconds and milliseconds of the current date
    currentDate.setHours(0, 0, 0, 0);

    // Calculate the difference in days
    const diffInDays = Math.floor(
        (inputDate.valueOf() - currentDate.valueOf()) / (1000 * 60 * 60 * 24),
    );

    // Format time
    const timeString = inputDate.toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
        // second: '2-digit'
    });

    // Format date based on the difference
    if (diffInDays === 0) {
        return `Today at ${timeString}`;
    } else if (diffInDays === -1) {
        return `Yesterday at ${timeString}`;
    } else {
        const year = inputDate.getFullYear();
        const month = inputDate.getMonth() + 1; // Months are 0-based
        const day = inputDate.getDate();
        return `${year}/${month}/${day} ${timeString}`;
    }
}

export const generateYAxis = (revenue: Revenue[]) => {
    // Calculate what labels we need to display on the y-axis
    // based on highest record and in 1000s
    const yAxisLabels = [];
    const highestRecord = Math.max(...revenue.map((month) => month.revenue));
    const topLabel = Math.ceil(highestRecord / 1000) * 1000;

    for (let i = topLabel; i >= 0; i -= 1000) {
        yAxisLabels.push(`$${i / 1000}K`);
    }

    return { yAxisLabels, topLabel };
};

export const generatePagination = (currentPage: number, totalPages: number) => {
    // If the total number of pages is 7 or less,
    // display all pages without any ellipsis.
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // If the current page is among the first 3 pages,
    // show the first 3, an ellipsis, and the last 2 pages.
    if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages - 1, totalPages];
    }

    // If the current page is among the last 3 pages,
    // show the first 2, an ellipsis, and the last 3 pages.
    if (currentPage >= totalPages - 2) {
        return [1, 2, '...', totalPages - 2, totalPages - 1, totalPages];
    }

    // If the current page is somewhere in the middle,
    // show the first page, an ellipsis, the current page and its neighbors,
    // another ellipsis, and the last page.
    return [
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages,
    ];
};

export const supportsCssVars: () => boolean = function (): boolean {
    document.documentElement.className = 'js';
    let temp: HTMLStyleElement = document.createElement('style');
    temp.innerHTML = 'root: { --tmp-var: bold; }';
    document.head.appendChild(temp);
    const isSupport: boolean = !!(
        window.CSS &&
        window.CSS.supports &&
        window.CSS.supports('font-weight', 'var(--tmp-var)')
    );
    temp.parentNode?.removeChild(temp);
    return isSupport;
};

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

import slugify from 'slugify';
import { PortableTextBlock } from 'next-sanity';

export function convertToSlug(
    text?: string,
    { fallback }: { fallback?: string } = { fallback: 'top-level' },
) {
    if (!text) return fallback;
    return slugify(text.trim(), {
        lower: true,
        remove: /[^a-zA-Z0-9 ]/g,
    });
}

export function parseChildrenToSlug(children: PortableTextBlock['children']) {
    if (!children) return '';
    return convertToSlug(children.map((child) => child.text).join(''));
}

import React from 'react';

/**
 * 将文本中的 {nowrap}...{/nowrap} 标记解析为 React 节点数组。
 * 标记内的内容会被包裹在 <span style={{whiteSpace:'nowrap'}}> 中，防止自动换行。
 * 用于中文 i18n 场景，替代英文的 \u00A0 方案。
 */
export function parseNowrap(text: string): React.ReactNode {
    const parts = text.split(/(\{nowrap\}[\s\S]*?\{\/nowrap\})/);
    if (parts.length === 1) return text;
    return parts.map((part, i) => {
        const match = part.match(/^\{nowrap\}([\s\S]*?)\{\/nowrap\}$/);
        if (match) {
            return React.createElement(
                'span',
                { key: i, style: { whiteSpace: 'nowrap' } },
                match[1],
            );
        }
        return part;
    });
}
